import React from 'react';
import { Link } from 'react-router-dom';
import Seo from '../../components/Seo';
import { ROUTES } from '../../enum/routes';

export default function PrivacyPage() {
	return (
		<>
			<Seo
				title='Privacy Policy | Epic Home Pros'
				description='Privacy Policy for Epic Home Pros. How we collect, use, and protect information when you request a remodel quote or visit epichomepros.com.'
				path={ROUTES.PRIVACY}
			/>
			<section className='bg-forest-tint py-16 sm:py-20'>
				<div className='container-page'>
					<div className='mx-auto max-w-3xl'>
						<div className='label-eyebrow'>Legal</div>
						<h1 className='h-display mt-3'>Privacy Policy</h1>
						<p className='mt-4 text-sm text-ink/60'>
							Last updated: September 3, 2026
						</p>
						<p className='mt-6 text-lg leading-relaxed text-ink/75'>
							Epic Home Pros (“we,” “us,” or “our”) respects your privacy. This
							Privacy Policy explains what information we collect when you visit
							epichomepros.com or contact us, how we use it, and the choices you
							have.
						</p>
					</div>
				</div>
			</section>
			<section className='py-14 sm:py-20'>
				<div className='container-page'>
					<div className='mx-auto max-w-3xl space-y-10'>
						<section>
							<h2 className='font-serif text-2xl leading-snug text-ink'>
								Who we are
							</h2>
							<div className='mt-4 space-y-4 text-base leading-relaxed text-ink/80'>
								<p>
									Epic Home Pros matches Texas homeowners with licensed local
									remodeling partners. We are not the install crew. This policy
									applies to our website, epichomepros.com, and to information
									you provide when you request a quote, book an estimate, or
									otherwise contact us.
								</p>
							</div>
						</section>
						<section>
							<h2 className='font-serif text-2xl leading-snug text-ink'>
								Information we collect
							</h2>
							<div className='mt-4 space-y-4 text-base leading-relaxed text-ink/80'>
								<p>
									<strong>Information you give us.</strong> When you submit our
									quote form, or contact us by phone or email, we may collect:
								</p>
								<ul className='list-disc space-y-2 pl-5 marker:text-forest'>
									<li>Your name</li>
									<li>Your phone number</li>
									<li>Your email address</li>
									<li>Your project or property address</li>
									<li>
										The service you are interested in and your service area
									</li>
									<li>Details about your project that you choose to share</li>
									<li>Your requested estimate appointment date and time</li>
								</ul>
								<p>
									<strong>Information collected automatically.</strong> Like
									most websites, we may collect device and browser type,
									approximate location, pages you view, and how you arrived at
									our site, through cookies and similar technologies.
								</p>
							</div>
						</section>
						<section>
							<h2 className='font-serif text-2xl leading-snug text-ink'>
								How we use your information
							</h2>
							<div className='mt-4 space-y-4 text-base leading-relaxed text-ink/80'>
								<p>We use the information we collect to:</p>
								<ul className='list-disc space-y-2 pl-5 marker:text-forest'>
									<li>Respond to your inquiries</li>
									<li>Match you with a licensed local remodeling partner</li>
									<li>
										Schedule, confirm, and follow up on estimate appointments
									</li>
									<li>Operate and improve our website</li>
									<li>Measure advertising performance</li>
									<li>Comply with legal obligations</li>
								</ul>
							</div>
						</section>
						<section>
							<h2 className='font-serif text-2xl leading-snug text-ink'>
								SMS / Text Messaging
							</h2>
							<div className='mt-4 space-y-4 text-base leading-relaxed text-ink/80'>
								<p>
									Epic Home Pros may send text messages to schedule and confirm
									your free estimate and to answer questions. This is a
									customer-care and transactional program. Message frequency
									varies based on your conversation with us.
								</p>
								<p>
									You provide consent when you submit your phone number on our
									form and agree to be contacted by text, or when you verbally
									agree during a phone call. You must be at least 18. Consent is
									not a condition of receiving a quote.
								</p>
								<p>
									You can cancel at any time by replying STOP. For help, reply
									HELP, or contact us at (844) 800-EPIC or
									hello@epichomepros.com. Message and data rates may apply.
								</p>
							</div>
						</section>
						<section>
							<h2 className='font-serif text-2xl leading-snug text-ink'>
								Mobile Opt-In and Consent: No Sharing for Marketing
							</h2>
							<div className='mt-4 space-y-4 text-base leading-relaxed text-ink/80'>
								<p>
									We treat your mobile number and text consent as private. We do
									not sell it, and we do not share it with third parties for
									their own marketing.
								</p>
								<div className='rounded-md border-l-4 border-forest bg-forest-tint p-4'>
									<p className='font-medium leading-relaxed text-ink'>
										No mobile information will be shared with third parties or
										affiliates for marketing or promotional purposes. Sharing
										with service providers strictly to send messages or provide
										customer service is permitted. Text messaging originator
										opt-in data and consent will not be shared with any third
										parties.
									</p>
								</div>
								<p>
									We may share your project details with a licensed local
									remodeling partner so they can contact you about an on-site
									estimate. That partner is not permitted to use your number for
									unrelated marketing.
								</p>
							</div>
						</section>
						<section>
							<h2 className='font-serif text-2xl leading-snug text-ink'>
								How we share your information
							</h2>
							<div className='mt-4 space-y-4 text-base leading-relaxed text-ink/80'>
								<p>
									<strong>We do not sell your personal information.</strong> We
									share it only as needed to run the match:
								</p>
								<ul className='list-disc space-y-2 pl-5 marker:text-forest'>
									<li>
										<strong>Remodeling partners</strong> who will quote your
										project
									</li>
									<li>
										<strong>Service providers</strong> that host the site, send
										email or texts, or measure ads
									</li>
									<li>
										<strong>Legal and safety reasons</strong> when required by
										law
									</li>
								</ul>
							</div>
						</section>
						<section>
							<h2 className='font-serif text-2xl leading-snug text-ink'>
								Your choices
							</h2>
							<div className='mt-4 space-y-4 text-base leading-relaxed text-ink/80'>
								<p>
									You may ask us what information we hold, request a correction
									or deletion (subject to record-keeping laws), or opt out of
									marketing. Contact hello@epichomepros.com or (844) 800-EPIC.
								</p>
							</div>
						</section>
						<section>
							<h2 className='font-serif text-2xl leading-snug text-ink'>
								Contact us
							</h2>
							<div className='mt-4 space-y-4 text-base leading-relaxed text-ink/80'>
								<p>
									<strong>Epic Home Pros</strong>
									<br />
									Email:{' '}
									<a
										href='mailto:hello@epichomepros.com'
										className='text-forest underline hover:text-forest-hover'
									>
										hello@epichomepros.com
									</a>
									<br />
									Phone:{' '}
									<a
										href='tel:+18448003742'
										className='text-forest underline hover:text-forest-hover'
									>
										(844) 800-EPIC
									</a>
									<br />
									Serving Austin, Dallas, Houston, and San Antonio
								</p>
								<p>
									See our{' '}
									<Link
										className='text-forest underline hover:text-forest-hover'
										to='/terms'
									>
										Terms &amp; Conditions
									</Link>
									.
								</p>
							</div>
						</section>
					</div>
				</div>
			</section>
		</>
	);
}
